import { Component, computed, OnDestroy, signal } from '@angular/core';
import { RouterLink } from '@angular/router';

import { TipoPropiedad } from '../../core/models/propiedad.model';

interface TileHome {
  label: string;
  imagen: string;
  /** Query params hacia el buscador. */
  query: Record<string, string>;
}

/** Pausa con el título quieto, antes de empezar a desvanecerlo (FP-41). */
const PAUSA_MS = 2200;

/**
 * Duración del fade. Tiene que coincidir con la `transition` de `.tagline`: es
 * lo que se espera para cambiar la palabra con la opacidad ya en 0.
 */
const FADE_MS = 500;

/**
 * Home de la web pública (Figma `home`, node 15-1205): tagline + grilla de
 * accesos por categoría ("Home tiles"). Cada tile navega al buscador con la
 * categoría preseleccionada, en vista grilla (desde ahí el alternador
 * "Ver mapa" lleva a la vista de mapa — FE-01).
 *
 * "Emprendimientos" no es una categoría sino un flag de la propiedad
 * (spec §7.1): el tile navega al buscador con `emprendimiento=1`, que por
 * ahora se filtra client-side (TODO soporte en el endpoint público).
 *
 * FP-41: la última palabra del tagline rota entre los tipos, y en cada cambio
 * se desvanece y vuelve el título completo. Las palabras salen de los tiles
 * —misma lista y mismo orden, porque son la misma cosa: lo que la home
 * ofrece—, así que sumar o sacar un tile la actualiza.
 */
@Component({
  selector: 'app-home-page',
  imports: [RouterLink],
  template: `
    <div class="home">
      <!-- FP-41: lo que cambia es el tipo, pero el fade se lo come el título
           entero, "Encontrá" incluido — así el gradiente queda como el Figma
           (el por qué está en los estilos). -->
      <h1 class="tagline" [class.tagline--oculto]="!tituloVisible()">
        Encontrá {{ tipo() }}
      </h1>

      <nav class="tiles" aria-label="Categorías de propiedades">
        @for (tile of tiles; track tile.label) {
          <a
            class="tile"
            [routerLink]="['/buscador']"
            [queryParams]="tile.query"
            [style.background-image]="'url(' + tile.imagen + ')'"
          >
            <span class="tile__label">{{ tile.label }}</span>
          </a>
        }
      </nav>
    </div>
  `,
  styles: `
    .home {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 48px;
      padding: 48px;
    }

    /* FP-41: el fade va en el h1 —se desvanece el título completo— y no en la
       palabra sola. Eso es deliberado: con el gradiente acá,
       background-clip: text lo recorta por TODOS los glifos del h1 y la
       opacidad de un span hijo no toca esa capa; envolviendo el tipo en un
       span, con opacity 0 la palabra se seguía viendo a full color (verificado
       en Chromium). Desvanecer sólo el tipo obliga a que cada parte pinte su
       propio gradiente, y ahí la rampa turquesa→violeta vuelve a arrancar en el
       tipo en vez de recorrer el título entero: producto eligió conservar la
       rampa del Figma (2026-08-18).
       Los 500ms tienen que coincidir con FADE_MS, que es lo que el componente
       espera para cambiar la palabra mientras no se ve. (Sin backticks: esto
       vive en un template literal.) */
    .tagline {
      margin: 0;
      text-align: center;
      font-size: 36px;
      font-weight: var(--fw-semibold);
      /* No es 1: con background-clip: text el gradiente se pinta en la caja del
         elemento, y con line-height 1 la caja mide justo el font-size, así que
         las colas de la "p" de "departamentos" quedaban fuera del recorte y se
         veían cortadas. 1.4 cubre el alto real de Open Sans (asc + desc ~
         1.36em). */
      line-height: 1.4;
      letter-spacing: -0.5px;
      background: var(--lg-gradient-title);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      color: transparent;
      transition: opacity 500ms ease;
    }

    .tagline--oculto {
      opacity: 0;
    }

    /* Con movimiento reducido el título no rota —lo corta el componente— y la
       transición no tiene nada que animar. */
    @media (prefers-reduced-motion: reduce) {
      .tagline {
        transition: none;
      }
    }

    .tiles {
      display: grid;
      grid-template-columns: repeat(4, 240px);
      gap: 16px;
    }

    .tile {
      position: relative;
      width: 240px;
      height: 240px;
      border-radius: 8px;
      overflow: hidden;
      background-size: cover;
      background-position: center;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.15s ease, box-shadow 0.15s ease;

      &:hover {
        transform: scale(1.03);
        box-shadow: var(--elevation-5);
      }
    }

    .tile__label {
      font-size: 24px;
      font-weight: var(--fw-semibold);
      line-height: 1.1;
      color: var(--c-neutrals-grey_0);
      text-align: center;
      text-shadow: 0 8px 16px rgba(10, 51, 50, 0.4);
    }

    @media (max-width: 1100px) {
      .tiles {
        grid-template-columns: repeat(2, minmax(160px, 240px));
      }
    }

    /* Mobile (Figma 157-762): grilla de 2 columnas con tiles cuadrados */
    @media (max-width: 900px) {
      .home {
        padding: 24px 16px;
        gap: 24px;
      }

      .tagline {
        font-size: 24px;
      }

      .tiles {
        grid-template-columns: 1fr 1fr;
        gap: 12px;
        width: 100%;
      }

      .tile {
        width: 100%;
        height: auto;
        aspect-ratio: 1;
      }

      .tile__label {
        font-size: 18px;
      }
    }

    /* RP-10: en los celulares angostos la etiqueta no entra en el tile.
       "Emprendimientos" es la más larga y no tiene dónde cortar —es una sola
       palabra—, así que a 18px pide 155px contra los 138 que mide el tile a
       320px de ancho, y el overflow: hidden se la come. A 14px pide 121 y entra
       con aire; de paso descomprime "Departamentos", que a 320px entraba por
       2px. Desde 375px las dos entran a 18px y el tile vuelve al tamaño del
       Figma. */
    @media (max-width: 360px) {
      .tile__label {
        font-size: var(--fsz-body_2);
      }
    }
  `,
})
export class HomePage implements OnDestroy {
  // Desde la home se aterriza en la grilla (vista=grilla); el alternador
  // del buscador permite pasar al mapa.
  readonly tiles: TileHome[] = [
    {
      label: 'Departamentos',
      imagen: 'img/home/departamentos.png',
      query: { categoria: TipoPropiedad.DEPARTAMENTO, vista: 'grilla' },
    },
    {
      label: 'Casas',
      imagen: 'img/home/casas.png',
      query: { categoria: TipoPropiedad.CASA, vista: 'grilla' },
    },
    {
      label: 'Industrias',
      imagen: 'img/home/industrias.png',
      query: { categoria: TipoPropiedad.INDUSTRIA, vista: 'grilla' },
    },
    {
      label: 'Terrenos',
      imagen: 'img/home/terrenos.png',
      query: { categoria: TipoPropiedad.TERRENO, vista: 'grilla' },
    },
    {
      label: 'Locales',
      imagen: 'img/home/locales.png',
      query: { categoria: TipoPropiedad.LOCAL, vista: 'grilla' },
    },
    {
      label: 'Oficinas',
      imagen: 'img/home/oficinas.png',
      query: { categoria: TipoPropiedad.OFICINA, vista: 'grilla' },
    },
    {
      label: 'Especiales',
      imagen: 'img/home/especiales.png',
      query: { categoria: TipoPropiedad.ESPECIAL, vista: 'grilla' },
    },
    {
      label: 'Emprendimientos',
      imagen: 'img/home/emprendimientos.png',
      query: { emprendimiento: '1', vista: 'grilla' },
    },
  ];

  // ── Tagline rotativo (FP-41) ───────────────────────────────────────────────

  /**
   * Los tipos en minúscula y en el orden de los tiles: "departamentos",
   * "casas", "industrias", … Salen de `tiles` para no mantener dos listas de
   * lo mismo, que es justo lo que se desincroniza.
   */
  private readonly tipos = this.tiles.map((t) => t.label.toLowerCase());

  private readonly indice = signal(0);

  /** Tipo que muestra el título ahora. */
  readonly tipo = computed(() => this.tipos[this.indice()]);

  /** false mientras el título se desvanece para dejar paso al siguiente tipo. */
  readonly tituloVisible = signal(true);

  private ciclo?: ReturnType<typeof setInterval>;
  private cambio?: ReturnType<typeof setTimeout>;

  constructor() {
    // Con movimiento reducido no rota: acá el efecto ES el cambio, no hay
    // versión suave que valga. El título queda fijo en "departamentos", que es
    // exactamente lo que decía antes de FP-41.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.ciclo = setInterval(() => {
      this.tituloVisible.set(false);
      // La palabra se cambia con la opacidad ya en 0: así el ancho del título
      // salta mientras no se ve y el fade-in entra con el texto centrado.
      this.cambio = setTimeout(() => {
        this.indice.update((i) => (i + 1) % this.tipos.length);
        this.tituloVisible.set(true);
      }, FADE_MS);
    }, PAUSA_MS + FADE_MS);
  }

  ngOnDestroy(): void {
    clearInterval(this.ciclo);
    clearTimeout(this.cambio);
  }
}
